//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { Edit, TrashCan, Close } from '@carbon/icons-react';
import { useTranslation } from 'react-i18next';
import { useUserAuth } from '../../UserAuth';
import { useTeamspaceContext } from '../../TeamspaceContext';
import { useConfig } from '../../Config';
import { WarningAlt } from '@carbon/icons-react';
import { Dialog, DialogContent } from '../ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '../ui/tooltip';
import CustomSnackbar, { useSnackbar } from '../common/CustomSnackbar';
import MCPDataService, {
  AICodingToolItem,
  AICodingToolUpsertPayload,
} from '../../utils/MCPDataService';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../ui/table';
import { cn } from '../../lib/utils';

interface CodingCLIFormData {
  tool_name: string;
  display_name: string;
  auth_type: string;
  api_key: string;
  enabled: boolean;
}

type CodingCLIFormErrors = Partial<Record<'tool_name' | 'api_key', string>>;

const INITIAL_FORM_DATA: CodingCLIFormData = {
  tool_name: '',
  display_name: '',
  auth_type: 'api_key',
  api_key: '',
  enabled: true,
};

function CodingCLITab(): JSX.Element {
  const [tools, setTools] = useState<AICodingToolItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [toolToDelete, setToolToDelete] = useState<AICodingToolItem | null>(
    null,
  );
  const [editingTool, setEditingTool] = useState<AICodingToolItem | null>(null);
  const [formData, setFormData] = useState<CodingCLIFormData>({
    ...INITIAL_FORM_DATA,
  });
  const [formErrors, setFormErrors] = useState<CodingCLIFormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslation();
  const { isAuthenticated, user } = useUserAuth();
  const { teamspaceId } = useTeamspaceContext();
  const { services } = useConfig();
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();
  const saveInProgressRef = useRef(false);

  const userRef = useRef(user);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const teamspaceIdRef = useRef(teamspaceId);
  const showSnackbarRef = useRef(showSnackbar);
  const tRef = useRef(t);

  useEffect(() => {
    userRef.current = user;
    isAuthenticatedRef.current = isAuthenticated;
    teamspaceIdRef.current = teamspaceId;
    showSnackbarRef.current = showSnackbar;
    tRef.current = t;
  }, [user, isAuthenticated, teamspaceId, showSnackbar, t]);

  const mcpDataService = useMemo(
    () =>
      new MCPDataService(
        () => userRef.current,
        () => isAuthenticatedRef.current,
        services?.analyticsai?.url,
      ),
    [services?.analyticsai?.url],
  );

  const fetchTools = useCallback(async () => {
    const currentTeamspaceId = teamspaceIdRef.current;
    if (!currentTeamspaceId) {
      setTools([]);
      return;
    }

    setIsLoading(true);
    try {
      const data = await mcpDataService.listAICodingTools(currentTeamspaceId);
      setTools(data || []);
    } catch (error) {
      console.error('Error fetching Coding CLI tools:', error);
      showSnackbarRef.current(
        tRef.current('error-fetching-coding-cli-tools') ||
          'Error fetching Coding CLI tools',
        'error',
      );
    } finally {
      setIsLoading(false);
    }
  }, [mcpDataService]);

  useEffect(() => {
    fetchTools();
  }, [fetchTools]);

  const openAddModal = () => {
    setEditingTool(null);
    setFormData({ ...INITIAL_FORM_DATA });
    setFormErrors({});
    setIsPanelOpen(true);
  };

  const openEditModal = (tool: AICodingToolItem) => {
    setEditingTool(tool);
    setFormData({
      tool_name: tool.tool_name,
      display_name: tool.display_name || tool.tool_name,
      auth_type: tool.auth_type,
      api_key: tool.api_key,
      enabled: tool.enabled,
    });
    setFormErrors({});
    setIsPanelOpen(true);
  };

  const closePanel = () => {
    if (saveInProgressRef.current) return;
    setIsPanelOpen(false);
    setEditingTool(null);
    setFormData({ ...INITIAL_FORM_DATA });
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: CodingCLIFormErrors = {};

    if (!formData.tool_name.trim()) {
      errors.tool_name = t('tool-name-required') || 'Tool Name is required';
    }

    if (!formData.api_key.trim()) {
      errors.api_key = t('api-key-is-required') || 'API Key is required';
    }

    if (!editingTool) {
      const exists = tools.some((row) => row.tool_name === formData.tool_name);
      if (exists) {
        errors.tool_name =
          t('coding-cli-tool-already-exists') ||
          'Tool is already configured for this team space';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const currentTeamspaceId = teamspaceIdRef.current;
    if (!currentTeamspaceId) {
      showSnackbar(
        t('select-teamspace-first') || 'Select a team space first',
        'error',
      );
      return;
    }

    saveInProgressRef.current = true;
    setIsSaving(true);
    try {
      if (editingTool) {
        const keyUnchanged = formData.api_key === editingTool.api_key;

        if (keyUnchanged) {
          if (editingTool.enabled !== formData.enabled) {
            await mcpDataService.toggleAICodingTool(
              currentTeamspaceId,
              editingTool.tool_name,
              { enabled: formData.enabled },
            );
            showSnackbar(
              t('coding-cli-updated-successfully') ||
                'Coding CLI updated successfully',
              'success',
            );
            await fetchTools();
          }
        } else {
          const payload: AICodingToolUpsertPayload = {
            tool_name: formData.tool_name,
            display_name: formData.tool_name,
            auth_type: formData.auth_type,
            api_key: formData.api_key,
            enabled: formData.enabled,
          };
          await mcpDataService.upsertAICodingTool(currentTeamspaceId, payload);
          showSnackbar(
            t('coding-cli-updated-successfully') ||
              'Coding CLI updated successfully',
            'success',
          );
          await fetchTools();
        }
      } else {
        const payload: AICodingToolUpsertPayload = {
          tool_name: formData.tool_name,
          display_name: formData.tool_name,
          auth_type: formData.auth_type,
          api_key: formData.api_key,
          enabled: formData.enabled,
        };
        await mcpDataService.upsertAICodingTool(currentTeamspaceId, payload);
        showSnackbar(
          t('coding-cli-created-successfully') ||
            'Coding CLI created successfully',
          'success',
        );
        await fetchTools();
      }

      saveInProgressRef.current = false;
      setIsSaving(false);
      closePanel();
    } catch (error: any) {
      const msg =
        error?.data?.message ||
        error?.data?.error ||
        (editingTool
          ? t('error-updating-coding-cli') || 'Error updating Coding CLI'
          : t('error-creating-coding-cli') || 'Error creating Coding CLI');
      showSnackbar(msg, 'error');
      saveInProgressRef.current = false;
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!toolToDelete) return;

    const currentTeamspaceId = teamspaceIdRef.current;
    if (!currentTeamspaceId) {
      showSnackbar(
        t('select-teamspace-first') || 'Select a team space first',
        'error',
      );
      return;
    }

    try {
      await mcpDataService.deleteAICodingTool(
        currentTeamspaceId,
        toolToDelete.tool_name,
      );
      await fetchTools();
      showSnackbar(
        `${toolToDelete.display_name} ${t('deleted-successfully')}`,
        'success',
      );
    } catch (error: any) {
      const msg =
        error?.data?.message ||
        error?.data?.error ||
        t('error-deleting-coding-cli') ||
        'Error deleting Coding CLI';
      showSnackbar(msg, 'error');
    } finally {
      setDeleteDialogOpen(false);
      setToolToDelete(null);
    }
  };

  const handleToggle = async (tool: AICodingToolItem) => {
    const currentTeamspaceId = teamspaceIdRef.current;
    if (!currentTeamspaceId) {
      showSnackbar(
        t('select-teamspace-first') || 'Select a team space first',
        'error',
      );
      return;
    }

    const previousTools = [...tools];
    const newEnabled = !tool.enabled;

    setTools((prev) =>
      prev.map((row) =>
        row.tool_name === tool.tool_name
          ? { ...row, enabled: newEnabled }
          : row,
      ),
    );

    try {
      await mcpDataService.toggleAICodingTool(
        currentTeamspaceId,
        tool.tool_name,
        {
          enabled: newEnabled,
        },
      );
      showSnackbar(
        `${tool.display_name} ${newEnabled ? t('activated') || 'activated' : t('deactivated') || 'deactivated'}`,
        'success',
      );
      await fetchTools();
    } catch (error) {
      setTools(previousTools);
      showSnackbar(
        t('error-updating-coding-cli-status') ||
          'Error updating Coding CLI status',
        'error',
      );
    }
  };

  const formatDate = (value?: string) => {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const inputClasses =
    'w-full bg-white dark:bg-carbon-darkPanel border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 placeholder-gray-400';

  return (
    <TooltipProvider>
      <div className="flex flex-col">
        <CustomSnackbar
          open={snackbarState.open}
          message={snackbarState.message}
          severity={snackbarState.severity}
          onClose={closeSnackbar}
        />

        <div className="mb-6 flex items-center justify-between gap-4">
          <h2 className="text-base font-bold text-carbon-headingText dark:text-carbon-bodyTextDark">
            {t('manage-coding-cli') || 'Manage Coding CLI'}
          </h2>

          <Button
            onClick={openAddModal}
            className="rounded bg-brand-interactive text-white hover:bg-primary-hover dark:bg-brand-interactive dark:hover:bg-brand-blue"
          >
            {t('add-cli') || 'Add CLI'}
          </Button>
        </div>

        <div className="table-container">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <span className="text-gray-500 dark:text-gray-400">
                {t('loading-trans')}
              </span>
            </div>
          ) : (
            <Table>
              <TableHeader className="table-header">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="font-bold">
                    {t('active') || 'Active'}
                  </TableHead>
                  <TableHead className="font-bold">{t('name')}</TableHead>
                  <TableHead className="font-bold">
                    {t('date-added') || 'Date Added'}
                  </TableHead>
                  <TableHead className="font-bold">{t('api-key')}</TableHead>
                  <TableHead className="font-bold">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tools.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center text-gray-500 dark:text-gray-400"
                    >
                      {t('no-coding-cli-configured') ||
                        'No Coding CLI configurations found'}
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {tools.map((tool) => (
                      <TableRow
                        key={tool.id}
                        className="table-row last:border-0"
                      >
                        <TableCell className="table-cell">
                          <Switch
                            checked={tool.enabled}
                            onCheckedChange={() => handleToggle(tool)}
                          />
                        </TableCell>

                        <TableCell className="table-cell text-xs">
                          {tool.display_name || tool.tool_name}
                        </TableCell>

                        <TableCell className="table-cell text-xs">
                          {formatDate(tool.created_at)}
                        </TableCell>

                        <TableCell className="table-cell font-mono text-xs">
                          {tool.api_key}
                        </TableCell>

                        <TableCell className="table-cell text-xs">
                          <div className="flex items-center gap-3 text-gray-600">
                            <button
                              onClick={() => openEditModal(tool)}
                              title={t('edit') || 'Edit'}
                              aria-label={t('edit') || 'Edit'}
                              className="hover:text-blue-600"
                            >
                              <Edit size={16} />
                            </button>

                            <button
                              onClick={() => {
                                setToolToDelete(tool);
                                setDeleteDialogOpen(true);
                              }}
                              title={t('delete') || 'Delete'}
                              aria-label={t('delete') || 'Delete'}
                              className="hover:text-red-600"
                            >
                              <TrashCan size={16} />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {isPanelOpen && (
          <>
            <div
              role="presentation"
              className="fixed inset-0 top-12 z-40 bg-black/20"
              onClick={closePanel}
              onKeyDown={(e) => {
                if (e.key === 'Escape') closePanel();
              }}
            />

            <div className="fixed bottom-0 right-0 top-12 z-50 flex w-[502px] flex-col border-l border-carbon-panelBorder bg-white shadow-xl dark:border-gray-700 dark:bg-carbon-darkPanel">
              <div className="flex items-center justify-between border-b border-gray-200 bg-carbon-lightHeader p-4 dark:border-gray-700 dark:bg-carbon-darkHeader">
                <h2 className="text-base font-semibold text-gray-700 dark:text-gray-300">
                  {editingTool
                    ? t('edit-coding-cli') || 'Edit Coding CLI'
                    : t('add-coding-cli') || 'Add Coding CLI'}
                </h2>
                <button
                  onClick={closePanel}
                  className="rounded p-1 transition-colors hover:bg-gray-300 dark:hover:bg-gray-600"
                  disabled={saveInProgressRef.current}
                  aria-label={t('close') || 'Close'}
                >
                  <Close
                    size={16}
                    className="text-gray-600 dark:text-gray-400"
                  />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <form className="space-y-6">
                  <div className="space-y-2">
                    <Label
                      htmlFor="coding-cli-tool-name"
                      className="text-gray-700 dark:text-gray-300"
                    >
                      {t('tool-name') || 'Tool Name'}{' '}
                      <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="coding-cli-tool-name"
                      type="text"
                      value={formData.tool_name}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          tool_name: e.target.value,
                          display_name: e.target.value,
                        });
                        setFormErrors((prev) => ({
                          ...prev,
                          tool_name: undefined,
                        }));
                      }}
                      placeholder={
                        t('enter-tool-name') ||
                        'e.g. GitHub Copilot, Claude CLI, IBM Bee'
                      }
                      disabled={Boolean(editingTool)}
                      className={cn(
                        inputClasses,
                        formErrors.tool_name &&
                          'border-red-500 focus-visible:ring-red-500',
                      )}
                    />
                    {formErrors.tool_name && (
                      <p className="text-xs text-red-500">
                        {formErrors.tool_name}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="coding-cli-auth-type"
                      className="text-gray-700 dark:text-gray-300"
                    >
                      {t('auth-type') || 'Auth Type'}
                    </Label>
                    <Input
                      id="coding-cli-auth-type"
                      type="text"
                      value={t('api-key') || 'API Key'}
                      readOnly
                      className={cn(inputClasses, 'cursor-default opacity-70')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="coding-cli-api-key"
                      className="text-gray-700 dark:text-gray-300"
                    >
                      {t('api-key')} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="coding-cli-api-key"
                      type="password"
                      value={formData.api_key}
                      onChange={(e) => {
                        setFormData({ ...formData, api_key: e.target.value });
                        setFormErrors((prev) => ({
                          ...prev,
                          api_key: undefined,
                        }));
                      }}
                      placeholder={t('enter-api-key') || 'Enter API key'}
                      className={cn(
                        inputClasses,
                        formErrors.api_key &&
                          'border-red-500 focus-visible:ring-red-500',
                      )}
                    />
                    {formErrors.api_key && (
                      <p className="text-xs text-red-500">
                        {formErrors.api_key}
                      </p>
                    )}
                    {editingTool && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('leave-masked-key-or-enter-new') ||
                          'Leave masked key as-is to keep current key, or enter a new key'}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="coding-cli-enabled"
                      className="text-gray-700 dark:text-gray-300"
                    >
                      {t('active') || 'Active'}
                    </Label>
                    <div className="flex h-10 items-center">
                      <Switch
                        checked={formData.enabled}
                        onCheckedChange={(value) => {
                          setFormData({ ...formData, enabled: value });
                        }}
                      />
                    </div>
                  </div>
                </form>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
                <Button
                  onClick={closePanel}
                  variant="outline"
                  disabled={saveInProgressRef.current}
                  className="w-[100px] border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {t('cancel')}
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="w-[100px] rounded bg-brand-interactive text-white hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-interactive dark:hover:bg-brand-blue"
                >
                  {isSaving
                    ? t('saving')
                    : editingTool
                      ? t('update')
                      : t('save')}
                </Button>
              </div>
            </div>
          </>
        )}

        <Dialog open={isDeleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="max-w-md pt-6">
            <div className="flex items-start gap-4">
              <WarningAlt size={20} className="mt-1 text-red-600" />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {t('confirm-deletion') || 'Confirm Deletion'}
                </h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {t('are-you-sure-delete-coding-cli') ||
                    `Are you sure you want to delete "${toolToDelete?.display_name}"?`}
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setToolToDelete(null);
                }}
                variant="outline"
              >
                {t('cancel') || 'Cancel'}
              </Button>
              <Button
                onClick={handleDelete}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                {t('yes-delete')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}

export default CodingCLITab;
